import React, { useState, useEffect } from 'react';
import useServiceWorker from '../../hooks/useServiceWorker';
import './PWAStatus.css';

/**
 * PWA Status Component
 * Shows connection status, update availability, and install prompt
 */
const PWAStatus = ({ showInstallPrompt = true, showUpdatePrompt = true, showOfflineStatus = true }) => {
  const {
    isOnline,
    isUpdateAvailable,
    isInstallable,
    isPWA,
    notificationPermission,
    installPWA,
    applyUpdate,
    enableNotifications
  } = useServiceWorker();

  const [installing, setInstalling] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [installPromptDismissed, setInstallPromptDismissed] = useState(false);

  // Check if install prompt was previously dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed === 'true') {
      setInstallPromptDismissed(true);
    }
  }, []);

  const handleInstall = async () => {
    setInstalling(true);
    const success = await installPWA();
    if (!success) {
      setInstalling(false);
    } else {
      // Mark as dismissed after successful install
      localStorage.setItem('pwa-install-dismissed', 'true');
      setInstallPromptDismissed(true);
    }
  };

  const handleDismissInstall = () => {
    localStorage.setItem('pwa-install-dismissed', 'true');
    setInstallPromptDismissed(true);
  };

  const handleUpdate = () => {
    setUpdating(true);
    applyUpdate();
  };

  const handleEnableNotifications = async () => {
    await enableNotifications();
  };

  if (isPWA && isOnline && !isUpdateAvailable) {
    return null; // Hide when everything is good and running as PWA
  }

  return (
    <div className="pwa-status">
      {/* Connection Status */}
      {showOfflineStatus && !isOnline && (
        <div className="pwa-status-item pwa-offline">
          <div className="pwa-status-icon">📡</div>
          <div className="pwa-status-content">
            <div className="pwa-status-title">Offline Mode</div>
            <div className="pwa-status-message">
              You're offline. Some features may be limited.
            </div>
          </div>
        </div>
      )}

      {/* Update Available */}
      {showUpdatePrompt && isUpdateAvailable && (
        <div className="pwa-status-item pwa-update">
          <div className="pwa-status-icon">🔄</div>
          <div className="pwa-status-content">
            <div className="pwa-status-title">Update Available</div>
            <div className="pwa-status-message">
              A new version is ready to install.
            </div>
          </div>
          <button 
            className="pwa-status-button pwa-button-primary"
            onClick={handleUpdate}
            disabled={updating}
          >
            {updating ? 'Updating...' : 'Update Now'}
          </button>
        </div>
      )}

      {/* Install Prompt - Show only once */}
      {showInstallPrompt && isInstallable && !isPWA && !installPromptDismissed && (
        <div className="pwa-status-item pwa-install">
          <div className="pwa-status-icon">📱</div>
          <div className="pwa-status-content">
            <div className="pwa-status-title">Install Zenvy</div>
            <div className="pwa-status-message">
              Install the app for a better experience with offline access.
            </div>
          </div>
          <div className="pwa-install-buttons">
            <button 
              className="pwa-status-button pwa-button-primary"
              onClick={handleInstall}
              disabled={installing}
            >
              {installing ? 'Installing...' : 'Install App'}
            </button>
            <button 
              className="pwa-status-button pwa-button-dismiss"
              onClick={handleDismissInstall}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* PWA Status Indicator */}
      {isPWA && (
        <div className="pwa-indicator">
          <span className="pwa-indicator-icon">✅</span>
          <span className="pwa-indicator-text">Running as App</span>
        </div>
      )}
    </div>
  );
};

export default PWAStatus;